import {
  GlobalSettingItemFragment,
  Maybe,
  PageCommunityDetail,
  SuburbFragment
} from '@src/graphql/gql-types';
import { HyperLink } from '@src/types/contentful';
import { getLinkData } from './url';
import { camelizeString } from './strings';

type Suburb = SuburbFragment & {
  communities: {
    title: string;
    slug: string;
  }[];
};

/**
 * Transforms communities data into a hierarchical navigation structure organized by state/territory.
 * Creates a nested structure of State -> Region -> Area -> Suburb with corresponding URLs.
 *
 * @param communities - Array of community details with suburb and location data
 * @param communitiesMegaMenuItem - Global settings containing base URL path
 * @returns Array of HyperLink objects with nested navigation structure. Each item contains:
 * - title: Display name
 * - urlPath: Full URL path
 * - openNewWindow: Link behavior flag
 * - subNav: Child navigation items
 */

export const transformCommunitiesByStateToHyperLinks = (
  communities: Maybe<PageCommunityDetail>[],
  communitiesMegaMenuItem: GlobalSettingItemFragment | undefined
): HyperLink[] => {
  const suburbsByCommunity = communities
    ?.map(
      (community) =>
        ({
          ...(community?.communitySuburb as SuburbFragment),
          communities: [
            {
              title: community?.title,
              slug: `${community?.slug}`
            }
          ]
        }) as Suburb
    )
    .filter((burb) => burb?.name)
    ?.reduce((acc: Suburb[], current: Suburb) => {
      const existingSuburb = acc.find((item) => item.name === current.name);

      if (existingSuburb) {
        existingSuburb.communities = [
          ...(existingSuburb.communities || []),
          ...current.communities
        ];
        return acc;
      }

      return [
        ...acc,
        {
          ...current,
          communities: current.communities
        }
      ];
    }, []);

  const navElementSlug = getLinkData([
    communitiesMegaMenuItem?.communityMegaMenu
  ])?.[0]?.urlPath;

  const transformToHyperLinks = suburbsByCommunity
    ?.map((suburb) => {
      const hasSingleCommunity = suburb?.communities?.length <= 1;
      const area = suburb?.linkedFrom?.areaCollection?.items[0];
      const region = area?.linkedFrom?.regionCollection?.items[0];
      const state = region?.linkedFrom?.stateCollection?.items[0];

      // Return structure of State -> Region -> Area -> Suburb
      return {
        // State
        title: state?.stateterritoryLabel,
        urlPath: `${navElementSlug}${state?.slug}`,
        openNewWindow: false,
        subNav: [
          // Region
          {
            title: region?.name,
            urlPath: `${navElementSlug}${state?.slug}/${region?.slug}`,
            openNewWindow: false,
            subNav: [
              // Area
              {
                title: area?.name,
                urlPath: `${navElementSlug}${state?.slug}/${region?.slug}/${area?.slug}`,
                openNewWindow: false,
                subNav: [
                  // Suburb
                  {
                    title: suburb?.name,
                    urlPath: hasSingleCommunity
                      ? `/community/${suburb?.communities?.[0]?.slug}`
                      : `${navElementSlug}${state?.slug}/${region?.slug}/${area?.slug}/${suburb?.slug}`,
                    openNewWindow: false
                  } as HyperLink
                ]
              } as HyperLink
            ]
          } as HyperLink
        ]
      } as HyperLink;
    })
    .filter(({ title }) => title);

  const mergeAllDuplicateStates = transformToHyperLinks
    ?.reduce((acc: HyperLink[], current) => {
      const existingState = acc.find((item) => item.title === current.title);

      if (existingState) {
        // Merge regions for existing state
        current.subNav?.forEach((currentRegion) => {
          const existingRegion = existingState.subNav?.find(
            (region) => region.title === currentRegion.title
          );

          if (existingRegion) {
            // Merge areas for existing region
            currentRegion.subNav?.forEach((currentArea) => {
              const existingArea = existingRegion.subNav?.find(
                (area) => area.title === currentArea.title
              );

              if (existingArea) {
                // Merge suburbs for existing area
                existingArea.subNav = [
                  ...(existingArea.subNav || []),
                  ...(currentArea.subNav || [])
                ];
              } else {
                // Add new area
                existingRegion.subNav = [
                  ...(existingRegion.subNav || []),
                  currentArea
                ];
              }
            });
          } else {
            // Add new region
            existingState.subNav = [
              ...(existingState.subNav || []),
              currentRegion
            ];
          }
        });
      } else {
        // Add new state
        acc.push(current);
      }

      return acc;
    }, [])
    ?.reverse();

  return mergeAllDuplicateStates || [];
};

/**
 * Gets the navigation items adjacent to the current item in a hierarchical menu structure.
 * This includes items above, below, previous (parent), and next (first child).
 * The hierarchy follows: states -> regions -> areas -> suburbs
 *
 * @param nav - Array of top-level (state) navigation items, where each item may contain nested subNav items
 * @param title - Title of the current navigation item to find adjacent items for
 * @returns Object containing:
 *          - current: Slugified title of current item
 *          - previous: Slugified title of parent item (undefined for states)
 *          - next: Slugified title of first child item (undefined for suburbs)
 *          - above: Slugified title of previous sibling item at same level
 *          - below: Slugified title of next sibling item at same level
 */
export const getAdjacentMegaMenuItems = (
  nav: HyperLink[],
  title: string,
  isSunshineCoast: boolean,
  type: 'state' | 'region' | 'area' | 'suburb'
):
  | {
      previous: string | undefined;
      next: string | undefined;
      above: string | undefined;
      below: string | undefined;
    }
  | undefined => {
  const state = nav
    ?.map((state, index) => {
      if (state.title === title) {
        return {
          previous: undefined,
          next: `${camelizeString(state.subNav?.[0]?.title || '')}${
            title === 'Queensland' ? '-region' : ''
          }`,
          above: camelizeString(nav?.[index - 1]?.title || ''),
          below: camelizeString(nav?.[index + 1]?.title || '')
        };
      }
    })
    .filter((item) => item !== undefined)?.[0];

  const region = nav
    ?.flatMap(
      (state) =>
        state.subNav?.map((region, index) => {
          if (region.title === title) {
            return {
              previous: camelizeString(state.title || ''),
              next: `${camelizeString(region.subNav?.[0]?.title || '')}${
                isSunshineCoast ? '-area' : ''
              }`,
              above: `${camelizeString(
                state.subNav?.[index - 1]?.title || ''
              )}${
                state.subNav?.[index - 1]?.title === 'Sunshine Coast'
                  ? '-region'
                  : ''
              }`,
              below: `${camelizeString(
                state.subNav?.[index + 1]?.title || ''
              )}${
                state.subNav?.[index + 1]?.title === 'Sunshine Coast'
                  ? '-region'
                  : ''
              }`
            };
          }
        })
    )
    .filter((item) => item !== undefined)?.[0];

  const area = nav
    ?.flatMap(
      (state) =>
        state.subNav?.flatMap(
          (region) =>
            region.subNav?.map((area, index) => {
              if (area.title === title) {
                return {
                  previous: `${camelizeString(region.title || '')}${
                    isSunshineCoast ? '-region' : ''
                  }`,
                  next: `${camelizeString(area.subNav?.[0]?.title || '')}`,
                  above: `${camelizeString(
                    region.subNav?.[index - 1]?.title || ''
                  )}${
                    region.subNav?.[index - 1]?.title === 'Sunshine Coast'
                      ? '-area'
                      : ''
                  }`,
                  below: `${camelizeString(
                    region.subNav?.[index + 1]?.title || ''
                  )}${
                    region.subNav?.[index + 1]?.title === 'Sunshine Coast'
                      ? '-area'
                      : ''
                  }`
                };
              }
            })
        )
    )
    .filter((item) => item !== undefined)?.[0];

  const suburb = nav
    ?.flatMap(
      (state) =>
        state.subNav?.flatMap(
          (region) =>
            region.subNav?.flatMap(
              (area) =>
                area.subNav?.map((suburb, index) => {
                  if (suburb.title === title) {
                    return {
                      previous: `${camelizeString(area.title || '')}${
                        area.title === 'Sunshine Coast' ? '-area' : ''
                      }`,
                      next: undefined,
                      above: camelizeString(
                        area.subNav?.[index - 1]?.title || ''
                      ),
                      below: camelizeString(
                        area.subNav?.[index + 1]?.title || ''
                      )
                    };
                  }
                })
            )
        )
    )
    .filter((item) => item !== undefined)?.[0];

  if (title === 'Sunshine Coast' && type === 'area') {
    return area;
  }

  return state || region || area || suburb;
};

/**
 * Groups navigation items from a hierarchical menu structure into separate arrays by their level.
 * The hierarchy follows: states -> regions -> areas -> suburbs
 *
 * @param nav - Array of top-level (state) navigation items, where each item may contain nested subNav items
 * @returns Object containing arrays of navigation items grouped by their level:
 *          - states: Original top-level navigation items
 *          - regions: All region-level items flattened into a single array
 *          - areas: All area-level items flattened into a single array
 *          - suburbs: All suburb-level items flattened into a single array
 */
export const groupMegaMenuItemsByContentType = (nav: HyperLink[]) => {
  const regions = nav?.flatMap((state) => state.subNav) as HyperLink[];
  const areas = nav?.flatMap(
    (state) => state.subNav?.flatMap((region) => region.subNav)
  ) as HyperLink[];
  const suburbs = nav?.flatMap(
    (state) =>
      state.subNav?.flatMap(
        (region) => region.subNav?.flatMap((area) => area.subNav)
      )
  ) as HyperLink[];
  return {
    states: nav,
    regions,
    areas,
    suburbs
  };
};

/**
 * Gets the adjacent navigation items (above and below) for a given item in a navigation menu.
 * Used for keyboard navigation to determine which items to focus when arrow keys are pressed.
 *
 * @param nav - Array of navigation items to search through
 * @param title - Title of the current navigation item to find adjacent items for
 * @returns Object containing the camelized titles of adjacent items, or undefined if no match found:
 *          - above: Title of the item above the current item (undefined if current item is first)
 *          - below: Title of the item below the current item (undefined if current item is last)
 */
export const getAdjacentSubNavItems = (
  nav: HyperLink[],
  title: string
):
  | {
      above: string | undefined;
      below: string | undefined;
    }
  | undefined =>
  nav
    ?.map((item, index) => {
      if (item.title === title) {
        return {
          above: camelizeString(nav?.[index - 1]?.title || ''),
          below: camelizeString(nav?.[index + 1]?.title || '')
        };
      }
    })
    .filter((item) => item !== undefined)?.[0];
